import { FeatureFlag, useFeatureFlag } from './useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'

import { useApolloClient } from '@apollo/client'

import { useCreateBulkIncidentActionMutation } from '@/graphql/generated/hooks'
import { CreateBulkIncidentActionInput } from '@/graphql/generated/schemas'
import { includeIfActive } from '@/utils/apollo'

const GET_INCIDENTS_QUERY_NAME = 'Incidents'
const INCIDENTS_TABLE_QUERY_NAME = 'IncidentsTable'

/**
 * Hook to determine if bulk actions are available for the current user
 * Checks both feature flag and user permissions
 */
export const useCanBulkAction = (): boolean => {
  const isBulkActionFeatureFlagOn: boolean = useFeatureFlag(
    FeatureFlag.bulkIncidentActions
  )
  const { canBulkAction } = usePermissions()
  return isBulkActionFeatureFlagOn && canBulkAction
}

export const useBulkAction = () => {
  const [bulkAction, { loading: isSubmitting }] =
    useCreateBulkIncidentActionMutation()
  const client = useApolloClient()

  const submitBulkAction = async (props: CreateBulkIncidentActionInput) => {
    return await bulkAction({
      variables: {
        input: props,
      },
      refetchQueries: [
        // The Incidents table runs IncidentsTable instead of Incidents while
        // noTotalIncidentCount is on (ENG-34177), so name both and let
        // includeIfActive pick whichever is mounted. Both are guarded: the
        // skipped one sits in Apollo's standby state, which
        // getObservableQueries('active') excludes, so naming it bare would
        // trip the "Unknown query" dev warning.
        ...includeIfActive(client, GET_INCIDENTS_QUERY_NAME),
        ...includeIfActive(client, INCIDENTS_TABLE_QUERY_NAME),
      ],
    })
  }

  return {
    submitBulkAction,
    isSubmitting,
  }
}
